import { Banknote, ChevronLeft, ChevronRight, Info, Pause, Play, RotateCcw, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import type { StrategyEntry } from "../../../services/investorStrategyService";
import profileConfigService from "../../../services/profileConfigService";
import trackingService from "../../../services/trackingService";
import { TrackingEvents } from "../../../services/trackingEvents";
import { AUTO_DEPLOY_CLARIFICATION_KEY } from "../../../utils/cashDeploymentSettings";
import { formatWeight } from "../../../utils/numberUtils";
import type {
  ProductLookupEntry,
  ProductLookupKey,
  StrategyDraftEntry,
} from "../../../utils/strategyUtils";
import { buildAllocationSplit } from "./allocationDriftDemo";
import { PIE_COLORS } from "./StrategyDonutCard";

interface AllocationMethodologyAckModalProps {
  isOpen: boolean;
  currentStrategies: StrategyEntry[];
  draftEntries: StrategyDraftEntry[];
  productLookup?: Map<ProductLookupKey, ProductLookupEntry>;
  /** Called after the acknowledgment persists, OR after "Continue anyway" on a failed persist. */
  onAcknowledged: () => void;
  /** Cancel: the save does not proceed. */
  onClose: () => void;
  appearance?: "default" | "alpha";
}

const LAST_STEP = 3;
const STEP_MS = 2400;

const AllocationMethodologyAckModal: React.FC<AllocationMethodologyAckModalProps> = ({
  isOpen,
  currentStrategies,
  draftEntries,
  productLookup,
  onAcknowledged,
  onClose,
  appearance = "default",
}) => {
  const isAlpha = appearance === "alpha";
  const { refreshUserConfig } = useAuth();

  const split = useMemo(
    () => buildAllocationSplit(currentStrategies, draftEntries, productLookup),
    [currentStrategies, draftEntries, productLookup]
  );

  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [persisting, setPersisting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedOnce, setFailedOnce] = useState(false);

  // Tracks whether the gate is still open, so a slow persist that resolves
  // after the user has closed it (Escape / X) does not advance to the save
  // confirmation behind their back. Guards the async write against a stale
  // resolution (repo async-race rule).
  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Reset the walkthrough each time the gate opens. It starts paused with an
  // explicit "press play" prompt so the animation reads as intentional, not
  // random (the user has to opt in to watch it).
  useEffect(() => {
    if (!isOpen) return;
    setStep(0);
    setPlaying(false);
    setHasStarted(false);
    setError(null);
    setFailedOnce(false);
    setPersisting(false);
  }, [isOpen]);

  const startPlayback = useCallback(() => {
    setHasStarted(true);
    setStep(0);
    setPlaying(true);
  }, []);

  // Auto-advance one step at a time while playing; stop at the last step.
  useEffect(() => {
    if (!isOpen || !playing) return;
    if (step >= LAST_STEP) {
      setPlaying(false);
      return;
    }
    const id = setTimeout(() => setStep((s) => Math.min(s + 1, LAST_STEP)), STEP_MS);
    return () => clearTimeout(id);
  }, [isOpen, playing, step]);

  // Escape maps to Cancel (no blocking browser dialogs).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const handleConfirm = useCallback(async () => {
    setPersisting(true);
    setError(null);
    try {
      await profileConfigService.patchUserSettings({
        [AUTO_DEPLOY_CLARIFICATION_KEY]: true,
      });
      trackingService.track(TrackingEvents.STRATEGY_ALLOCATION_ACK_CONFIRM, {});
      // Best-effort refresh; the authoritative write already succeeded, so a
      // refresh failure must not block the user.
      try {
        await refreshUserConfig();
      } catch {
        /* ignore refresh-only failure */
      }
      // If the user closed the gate (Escape / X) while the write was in
      // flight, do NOT advance to the save confirmation. The acknowledgment
      // is still persisted, so a later save skips the gate.
      if (!isOpenRef.current) return;
      onAcknowledged();
    } catch (err) {
      trackingService.track(TrackingEvents.STRATEGY_ALLOCATION_ACK_ERROR, {
        message: err instanceof Error ? err.message : String(err),
      });
      setError(
        "We couldn't save your acknowledgment. You can try again, or continue anyway."
      );
      setFailedOnce(true);
    } finally {
      setPersisting(false);
    }
  }, [refreshUserConfig, onAcknowledged]);

  const handleContinueAnyway = useCallback(() => {
    trackingService.track(TrackingEvents.STRATEGY_ALLOCATION_ACK_PROCEED_UNSAVED, {});
    onAcknowledged();
  }, [onAcknowledged]);

  if (!isOpen) return null;

  const showingCurrent = step >= 1;
  const showDeposit = step >= 2;
  const total = split.slices.reduce(
    (sum, slice) => sum + (showingCurrent ? slice.currentPct : slice.setPct),
    0
  );
  const widthFor = (value: number): number => (total > 0 ? (value / total) * 100 : 0);

  const stepTitle = (): string => {
    if (step === 0) return "The split you set";
    if (step === 1) return split.hasDrift ? "It drifts to your current split" : "Your current split";
    if (step === 2) return "New cash arrives";
    return "Auto-deployed into your current %";
  };

  const stepCaption = (): React.ReactNode => {
    if (step === 0) return "This is the split you set when you built your strategy.";
    if (step === 1) {
      return split.hasDrift
        ? "As prices move, your holdings drift. This is where your money sits today."
        : "Your holdings have not drifted yet, so your current split still matches what you set.";
    }
    if (step === 2) return "When you add cash, it does not reset to your original split.";
    return (
      <>
        <strong className={`font-bold underline decoration-2 underline-offset-2 ${isAlpha ? "text-v7-ink decoration-v7-bronze" : "text-gray-900 decoration-rp-amber dark:text-white"}`}>
          New cash is automatically deployed into your current percentages
        </strong>
        , so it follows where your money sits today, not the percentages you set at the start.
      </>
    );
  };

  return (
    <div className={`fixed inset-0 z-[60] flex items-center justify-center p-4 ${isAlpha ? "bg-v7-ink/70 backdrop-blur-sm" : "bg-black/70"}`}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="How your cash is allocated"
        data-testid="allocation-methodology-modal-panel"
        data-appearance={appearance}
        className={`flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden border shadow-2xl ${
          isAlpha
            ? "rounded-v7-popover border-v7-ink/15 bg-v7-white font-v7-sans text-v7-ink"
            : "rounded-xl border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface-raised"
        }`}
      >
        <div className={`flex items-start justify-between border-b px-5 py-4 ${isAlpha ? "border-v7-ink/15" : "border-gray-200 dark:border-rp-separator"}`}>
          <div>
            <h2 className={isAlpha ? "text-xl font-medium leading-tight tracking-[-0.02em] text-v7-ink" : "text-base font-bold leading-tight text-gray-900 dark:text-white"}>
              How your cash is allocated
            </h2>
            <p className={`mt-1 text-sm ${isAlpha ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-300"}`}>
              When new cash arrives, it is invested to match your current split, where your money
              sits today, not the percentages you set at the start.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={isAlpha
              ? "rounded-v7-control p-1 text-v7-ink/60 transition-colors hover:bg-v7-paper hover:text-v7-ink"
              : "rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-rp-surface dark:hover:text-white"}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto p-5">
          {!hasStarted && (
            <div className={`flex items-start gap-2 border px-3 py-2.5 text-sm ${isAlpha ? "rounded-v7-control border-v7-bronze/40 bg-v7-bronze/10 text-v7-ink-soft" : "rounded-lg border-rp-amber/40 bg-rp-amber/10 text-gray-700 dark:text-gray-200"}`}>
              <Info className={`mt-0.5 h-4 w-4 flex-shrink-0 ${isAlpha ? "text-v7-bronze-dark" : "text-rp-amber"}`} />
              <span>
                Please take a quick second to understand how cash allocation happens. Press play to
                watch the short walkthrough.
              </span>
            </div>
          )}
          <div className={`border p-5 ${isAlpha ? "rounded-v7-panel border-v7-ink/15 bg-v7-paper" : "rounded-2xl border-gray-200 bg-gray-50 dark:border-rp-separator dark:bg-rp-surface-sunken"}`}>
            <div className="mb-2 flex items-center justify-between">
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}`}>
                {stepTitle()}
              </span>
              {showDeposit && (
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${isAlpha ? "border-v7-success/30 bg-v7-success/10 text-v7-success" : "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400"}${
                    step === 2 ? " animate-pulse" : ""
                  }`}
                >
                  <Banknote className="h-3.5 w-3.5" />
                  New cash
                </span>
              )}
            </div>

            <div className="relative">
              <div className={`flex h-12 w-full overflow-hidden border ${isAlpha ? "rounded-v7-control border-v7-ink/15" : "rounded-lg border-gray-200 dark:border-rp-separator"}`}>
                {split.slices.map((slice, idx) => {
                  const value = showingCurrent ? slice.currentPct : slice.setPct;
                  return (
                    <div
                      key={slice.key}
                      className="flex h-full items-center justify-center overflow-hidden text-[11px] font-semibold text-black transition-all duration-700 ease-out"
                      style={{
                        width: `${widthFor(value)}%`,
                        backgroundColor: PIE_COLORS[idx % PIE_COLORS.length],
                      }}
                      title={`${slice.label} ${formatWeight(value)}%`}
                    >
                      {widthFor(value) >= 12 ? `${formatWeight(value)}%` : ""}
                    </div>
                  );
                })}
              </div>
              {!hasStarted && (
                <button
                  type="button"
                  onClick={startPlayback}
                  className={`absolute inset-0 flex items-center justify-center gap-2 text-sm font-semibold backdrop-blur-[1px] transition-colors ${isAlpha ? "rounded-v7-control bg-v7-ink/70 text-v7-white hover:bg-v7-ink/80" : "rounded-lg bg-black/40 text-white hover:bg-black/55"}`}
                  aria-label="Play walkthrough"
                >
                  <Play className="h-4 w-4" />
                  Press play
                </button>
              )}
            </div>

            <div className="mt-3 space-y-1.5">
              {split.slices.map((slice, idx) => (
                <div key={slice.key} className="flex items-center justify-between gap-2 text-xs">
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }}
                    />
                    <span className={`truncate ${isAlpha ? "text-v7-ink-soft" : "text-gray-700 dark:text-gray-300"}`}>{slice.label}</span>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2 font-mono">
                    {split.hasDrift && (
                      <span className={isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-gray-500"}>
                        set {formatWeight(slice.setPct)}%
                      </span>
                    )}
                    <span className={isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}>
                      now {formatWeight(slice.currentPct)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className={`min-h-[2.5rem] text-sm ${isAlpha ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-300"}`}>{stepCaption()}</p>

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => (hasStarted ? setPlaying((p) => !p) : startPlayback())}
                className={isAlpha
                  ? "inline-flex items-center gap-1.5 rounded-v7-control border border-v7-ink/30 bg-v7-white px-3 py-1.5 text-xs font-medium text-v7-ink transition-colors hover:border-v7-ink"
                  : "inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:border-gray-300 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"}
                aria-label={playing ? "Pause" : "Play"}
              >
                {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                {playing ? "Pause" : "Play"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setHasStarted(true);
                  setPlaying(false);
                  setStep((s) => Math.max(s - 1, 0));
                }}
                disabled={step === 0}
                className={isAlpha
                  ? "inline-flex items-center rounded-v7-control border border-v7-ink/30 bg-v7-white p-1.5 text-v7-ink transition-colors hover:border-v7-ink disabled:cursor-not-allowed disabled:opacity-40"
                  : "inline-flex items-center rounded-lg border border-gray-200 bg-white p-1.5 text-gray-700 transition-colors hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-40 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"}
                aria-label="Previous step"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setHasStarted(true);
                  setPlaying(false);
                  setStep((s) => Math.min(s + 1, LAST_STEP));
                }}
                disabled={step >= LAST_STEP}
                className={isAlpha
                  ? "inline-flex items-center rounded-v7-control border border-v7-ink/30 bg-v7-white p-1.5 text-v7-ink transition-colors hover:border-v7-ink disabled:cursor-not-allowed disabled:opacity-40"
                  : "inline-flex items-center rounded-lg border border-gray-200 bg-white p-1.5 text-gray-700 transition-colors hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-40 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"}
                aria-label="Next step"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={startPlayback}
                className={isAlpha
                  ? "inline-flex items-center gap-1.5 rounded-v7-control border border-v7-ink/30 bg-v7-white px-3 py-1.5 text-xs font-medium text-v7-ink transition-colors hover:border-v7-ink"
                  : "inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:border-gray-300 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Replay
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              {[0, 1, 2, 3].map((index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    setHasStarted(true);
                    setPlaying(false);
                    setStep(index);
                  }}
                  aria-label={`Step ${index + 1}`}
                  aria-current={index === step ? "step" : undefined}
                  className={`h-2 w-2 rounded-full transition-colors ${
                    index === step
                      ? isAlpha ? "bg-v7-bronze-dark" : "bg-rp-amber"
                      : isAlpha ? "bg-v7-ink/20" : "bg-gray-300 dark:bg-gray-600"
                  }`}
                />
              ))}
            </div>
          </div>

          {error && (
            <div className={`border p-3 text-sm ${isAlpha ? "rounded-v7-control border-v7-rust/30 bg-v7-rust/5 text-v7-rust" : "rounded-lg border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"}`}>
              {error}
            </div>
          )}
        </div>

        <div className={`flex flex-col-reverse items-stretch justify-end gap-3 border-t px-5 py-4 sm:flex-row sm:items-center ${isAlpha ? "border-v7-ink/15 bg-v7-paper/50" : "border-gray-200 dark:border-rp-separator"}`}>
          <button
            type="button"
            onClick={onClose}
            disabled={persisting}
            className={isAlpha
              ? "min-h-10 rounded-v7-control border border-v7-ink/30 bg-v7-white px-4 text-sm font-medium text-v7-ink transition-colors hover:border-v7-ink disabled:cursor-not-allowed disabled:opacity-60"
              : "rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"}
          >
            Cancel
          </button>
          {failedOnce && (
            <button
              type="button"
              onClick={handleContinueAnyway}
              className={isAlpha
                ? "min-h-10 rounded-v7-control border border-v7-ink/30 bg-v7-white px-4 text-sm font-medium text-v7-ink transition-colors hover:border-v7-ink"
                : "rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-400 dark:border-gray-600 dark:bg-rp-surface dark:text-gray-200 dark:hover:border-gray-500"}
            >
              Continue anyway
            </button>
          )}
          <button
            type="button"
            onClick={handleConfirm}
            disabled={persisting}
            className={isAlpha
              ? "min-h-10 rounded-v7-control bg-v7-ink px-4 text-sm font-medium text-v7-white transition-colors hover:bg-v7-ink-soft disabled:cursor-not-allowed disabled:bg-v7-mist disabled:text-v7-ink/60"
              : "rounded-lg bg-rp-amber px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-60"}
          >
            {persisting ? "Saving..." : "I understand, continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllocationMethodologyAckModal;
